import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, Flag, Tooltip} from 'platform/components';
import {Box, Space, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {VehicleInfo} from '../../components/VehicleInfo/VehicleInfo';
import {useCatalogue} from '../../hooks/useCatalogue';
import {usePriceReport} from '../../hooks/usePriceReport';
import i18n from '../../i18n/i18n';
import {DrillDownLevelName} from '../../types/api/drillDown';
import {convertMileageToAlphaMileageRange} from '../../utils/convertMileageToAlphaMileageRange';
import {DrillDown} from './components/DrillDown/DrillDown';

export function DrillDownPage(props: TestIdProps) {
  const {priceReport} = usePriceReport();
  const catalogue = useCatalogue();

  if (!priceReport) {
    return null;
  }

  const hasModelFamilyGroup = isNotNil(priceReport?.vehicle.modelFamilyGroup);
  const hiddenLevels: DrillDownLevelName[] = hasModelFamilyGroup
    ? ['make', 'model_family_group']
    : ['make', 'model_family_group', 'model'];
  const permanentFilters = hasModelFamilyGroup
    ? {
        make: priceReport.vehicle.make,
        model_family_group: priceReport.vehicle.modelFamilyGroup!,
      }
    : {
        make: priceReport.vehicle.make,
        model: priceReport.vehicle.modelFamily,
      };

  return (
    <>
      <Helmet title={i18n.t('page.drillDown.title')} />
      <VStack align="center">
        <Box width="100%" maxWidth={300} padding={2}>
          <Card>
            <VehicleInfo catalogue={catalogue} vehicle={priceReport.vehicle}>
              <Tooltip label={i18n.t('page.drillDown.liveDataTooltip')}>
                <Flag label={i18n.t('page.drillDown.liveData')} colorScheme="green" />
              </Tooltip>
            </VehicleInfo>
            <Space vertical={6} />
            <DrillDown
              regions={[priceReport.market]}
              currency={priceReport.sellingPrice.recommended.currency}
              hiddenLevels={hiddenLevels}
              permanentFilters={permanentFilters}
              selectedRow={{
                make: priceReport.vehicle.make,
                model_family_group: priceReport.vehicle.modelFamilyGroup ?? undefined,
                model: priceReport.vehicle.modelFamily,
                year: priceReport.vehicle.firstRegistrationOnYear?.toString(),
                fuel_type: priceReport.vehicle.fuelType,
                drive: priceReport.vehicle.drive,
                transmission: priceReport.vehicle.transmission,
                power: (priceReport.vehicle.maxPower ?? priceReport.vehicle.power)?.toString(),
                mileage_range: convertMileageToAlphaMileageRange(priceReport.vehicle.mileage),
                car_style: priceReport.vehicle.bodyStyle,
              }}
              scrollToSelectedRow
              data-testid={suffixTestId('drilldown', props)}
            />
          </Card>
        </Box>
      </VStack>
    </>
  );
}
